import { getHrtsByIds } from "@/lib/actions/actions";
import { datadogLogs } from "@datadog/browser-logs";
import { DiscountType } from "@frontend/lib/graphql/generated-gql";
import { roundToNearestCent } from "@frontend/lib/pricing/round-to-cent";
import { isStorefrontPricingShadowMode } from "@frontend/lib/pricing/storefront-pricing-mode";
import { getCartPrice } from "@frontend/lib/queries/cart/getCartPrice";
import type { RequestSource } from "@frontend/lib/schemas/clientSchema";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import type { CartItem } from "@frontend/lib/stores/slices/pricingMapSlice";
import type { HrtExtended } from "@frontend/lib/stores/slices/procedureSlice";
import { useQuery } from "@tanstack/react-query";
import { bundleToHrts } from "../directories/shop/[clientSlug]/products/[productId]/bundleConstants";
import {
  type DiscountData,
  useDiscountCode,
} from "../directories/shop/[clientSlug]/review/DiscountCodeContext";

interface UseCartItemResult {
  cartItems: CartItem[];
  isLoading: boolean;
  isLoadingCartPrice: boolean;
  /**
   * Shadow mode only: the cart total, in dollars, priced with the new rules
   * (per-item cent rounding, discounts on self-pay items only) while the
   * cart items above still carry the old math. Logged next to hex-pc's quote,
   * never charged or shown.
   */
  shadowTotalPrice?: number;
}

// Discount codes only ever apply to self-pay (CASH) items, matching hex-pc,
// which discounts only SELFPAY / SELFPAYOVERRIDE bought treatments. A discount
// shown on an insured item would be refused at checkout by the server quote.
export function isDiscountablePaymentMode(paymentMode: string): boolean {
  return paymentMode === "CASH";
}

/**
 * The discount math as it was before the per-item cent rounding and the
 * self-pay-only rule. Shadow mode keeps showing these prices; the enforced mode
 * uses applyDiscount below.
 */
export function applyDiscountLegacy(
  price: number,
  hrtId: number,
  isDiscountApplied: boolean,
  discountData: DiscountData | undefined,
  remainingDollarDiscount: number = discountData?.discountDollarAmount ?? 0,
): { price: number; isDiscounted: boolean } {
  if (!isDiscountApplied || !discountData)
    return { price, isDiscounted: false };

  const isHrtEligible =
    discountData.appliesToHrts.length === 0 ||
    discountData.appliesToHrts.includes(hrtId);

  if (!isHrtEligible) return { price, isDiscounted: false };

  if (discountData.discountType === DiscountType.Dollar) {
    const dollarDiscount = Math.min(
      Math.max(0, remainingDollarDiscount),
      price,
    );
    if (dollarDiscount <= 0) {
      return { price, isDiscounted: false };
    }
    return {
      price: price - dollarDiscount,
      isDiscounted: true,
    };
  }

  if (discountData.discountPercent <= 0) {
    return { price, isDiscounted: false };
  }

  return {
    price: price * (1 - discountData.discountPercent / 100),
    isDiscounted: true,
  };
}

export function applyDiscount(
  price: number,
  hrtId: number,
  isDiscountApplied: boolean,
  discountData: DiscountData | undefined,
  // A DOLLAR code carries a single cart-wide face value, not a per-item amount.
  // `remainingDollarDiscount` is the budget left after earlier eligible items
  // have consumed their share; the caller threads it through the cart so N
  // items don't each get the full amount off. It defaults to the full face
  // value so single-item callers (and unit tests) behave exactly as before.
  remainingDollarDiscount: number = discountData?.discountDollarAmount ?? 0,
): { price: number; isDiscounted: boolean } {
  if (!isDiscountApplied || !discountData)
    return { price, isDiscounted: false };

  const isHrtEligible =
    discountData.appliesToHrts.length === 0 ||
    discountData.appliesToHrts.includes(hrtId);

  if (!isHrtEligible) return { price, isDiscounted: false };

  // Every discounted price is rounded to the cent per item with the exact
  // formulas hex-pc uses, so the displayed cart total equals hex-pc's quote to
  // the cent and the charge, intent and saved treatments carry one amount.
  const basePrice = roundToNearestCent(price);

  if (discountData.discountType === DiscountType.Dollar) {
    // Take at most this item's price out of the remaining budget so the
    // discount never pushes the price negative and never exceeds the code's
    // face value across the whole cart.
    const dollarDiscount = roundToNearestCent(
      Math.min(Math.max(0, remainingDollarDiscount), basePrice),
    );
    if (dollarDiscount <= 0) {
      return { price, isDiscounted: false };
    }
    return {
      price: roundToNearestCent(basePrice - dollarDiscount),
      isDiscounted: true,
    };
  }

  if (discountData.discountPercent <= 0) {
    return { price, isDiscounted: false };
  }

  const percentDiscount = basePrice * (discountData.discountPercent / 100);
  return {
    price: Math.max(0, roundToNearestCent(basePrice - percentDiscount)),
    isDiscounted: true,
  };
}

export function useCartItem(
  hrtIds: number[],
  orgId: number,
  priceAsCart = false,
): UseCartItemResult {
  const consumer = useBoundStore.use.consumer();
  const user = useBoundStore.use.user();
  const temporarySlot = useBoundStore.use.temporarySlot();
  const insurances = useBoundStore.use.insurances();
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const isInsuranceValidated = useBoundStore.use.isInsuranceValidated();
  const storefrontPaymentMode = useBoundStore.use.storefrontPaymentMode();

  const priceMap = useBoundStore.use.pricingMapEntries();
  const { discountData, isDiscountApplied } = useDiscountCode();

  // Get all bundle HRTs by checking each hrtId
  const hrtIdsToFetch = hrtIds.reduce<number[]>((acc, hrtId) => {
    const bundledHrtIds = bundleToHrts.get(hrtId);
    if (bundledHrtIds) {
      // Skip the bundle HRT itself and add its bundled HRTs
      for (const id of bundledHrtIds) {
        acc.push(id);
      }
      return acc;
    }
    // If not a bundle, add the original HRT
    acc.push(hrtId);
    return acc;
  }, []);

  const { data: hrtData, isLoading } = useQuery({
    queryKey: ["getHrtsByIds", hrtIdsToFetch, orgId],
    queryFn: () => getHrtsByIds(hrtIdsToFetch, orgId),
    enabled: !!hrtIdsToFetch.length,
  });

  // Serialize insurances to stable primitives for the query key so it does not
  // change when async coverage fields (status/planName) populate on the raw
  // insurance objects — otherwise React Query v5 (which hashes keys by value)
  // re-keys mid-flow, cancels the first fetch and fires a second. Mirrors
  // PriceLoader's insurancesKey.
  const insurancesKey = JSON.stringify(
    (isPendingPricesMode ? [] : insurances).map((i) => ({
      payer: i.payer,
      memberId: i.memberId,
      state: i.state,
    })),
  );

  const { data: finalCart, isLoading: isLoadingCartPrice } = useQuery({
    queryKey: [
      "getCartPrice",
      hrtIdsToFetch,
      orgId,
      consumer.fname,
      consumer.lname,
      consumer.dob,
      // Stable primitive projection of insurances (see insurancesKey above).
      // When pending, this serializes `[]` so the key still changes vs.
      // non-pending mode (isPendingPricesMode is also a key member below).
      insurancesKey,
      user.email,
      temporarySlot?.practiceId,
      temporarySlot?.providerId,
      isPendingPricesMode,
    ],
    queryFn: () =>
      getCartPrice({
        fname: consumer.fname ?? "",
        lname: consumer.lname ?? "",
        dob: consumer.dob ?? "",
        // Pass empty array when in pending prices mode to only calculate cash prices
        insurances: isPendingPricesMode ? [] : insurances,
        npiId: "",
        providerLastName: "",
        orgId: orgId,
        hrts: hrtIdsToFetch.map((hrtId) => ({ hrtId })),
        requestSource: "STOREFRONT" as RequestSource,
        email: user.email ?? "",
        practiceId: temporarySlot?.practiceId ?? undefined,
        providerId: temporarySlot?.providerId
          ? Number(temporarySlot.providerId)
          : undefined,
        forceGenerateInsuredPricedTreatments: false,
      }),
    enabled:
      !!consumer.fname &&
      !!consumer.lname &&
      !!consumer.dob &&
      // Skip cart pricing call when in pending prices mode
      !isPendingPricesMode &&
      insurances.length > 0 &&
      // Gate on coverage being done — mirror EligibilityButton's product-page
      // gate and Booking's network check (isInsuranceValidated &&
      // !isPendingPricesMode). This query is INSURED-only (it already requires
      // insurances.length > 0 and passes the insurances array), so waiting for
      // eligibility here does not delay cash pricing — the cash price comes
      // from PriceLoader's price map, and this query stays off entirely when
      // there is no insurance.
      isInsuranceValidated &&
      hrtIdsToFetch.length > 0 &&
      priceAsCart,
    // Scoped guardrails for the pricing round-trip — see PriceLoader for the
    // rationale (coalesce in-flight duplicates, no focus/reconnect refetch).
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (priceMap[hrtIds[0]] === undefined) {
    return {
      cartItems: [],
      isLoading: true,
      isLoadingCartPrice: isLoadingCartPrice,
    };
  }

  // Shared budget for a cart-wide DOLLAR discount code, drawn down as each
  // eligible item consumes its share (see applyDiscount). PERCENT codes apply
  // independently per item and never touch this.
  let remainingDollarDiscount = discountData?.discountDollarAmount ?? 0;

  // Shadow mode keeps the old math on screen. Alongside it, the same cart is
  // priced with the new rules so the two can be compared with hex-pc's quote.
  const shadowMode = isStorefrontPricingShadowMode();
  let shadowRemainingDollarDiscount = discountData?.discountDollarAmount ?? 0;
  let shadowTotalPrice = 0;

  const cartItems: CartItem[] = hrtData
    ? hrtIdsToFetch.map((currentHrtId) => {
        // Find the HRT data for this ID
        const hrt = hrtData.find((h) => h.hrtId === currentHrtId);
        const finalCartItem = finalCart?.find(
          (item) => item.hrtId === currentHrtId,
        );
        if (!hrt) {
          datadogLogs.logger.error("HRT data not found for ID", {
            hrtId: currentHrtId,
          });
          throw new Error("HRT data not found");
        }

        // Find the original bundle HRT ID if this HRT is part of a bundle
        const originalHrtId =
          hrtIds.find((id) => {
            const bundled = bundleToHrts.get(id);
            return bundled?.includes(currentHrtId) || id === currentHrtId;
          }) ?? currentHrtId;

        // If user selection from storefrontPaymentMode is available, then use it, otherwise fallback to the priceMap.mode
        const selectedPaymentMode = priceMap[originalHrtId]?.mode;

        let priceData = priceMap[hrt.hrtId]?.priceData[selectedPaymentMode];
        let pricedTreatmentId = priceData?.pricedTreatmentId;
        let paymentType = selectedPaymentMode;

        // Get the original CASH price for comparison
        const originalCashPrice = priceMap[hrt.hrtId].priceData.CASH?.price;

        // Fallback: If INSURED mode is selected but INSURED price data is missing, default to CASH prices
        // because they should always be there by default
        if (!priceData && selectedPaymentMode === "INSURED") {
          priceData = priceMap[hrt.hrtId].priceData.CASH;
          pricedTreatmentId = priceData?.pricedTreatmentId;
          paymentType = "CASH";
        }

        if (!pricedTreatmentId) {
          datadogLogs.logger.error("Priced treatment ID not found", {
            hrtId: hrt.hrtId,
            paymentType,
            priceData: priceMap[hrt.hrtId].priceData,
          });
          throw new Error("Priced treatment ID not found");
        }

        if (!priceData) {
          datadogLogs.logger.error("Price data not found", {
            hrtId: hrt.hrtId,
            paymentType,
            priceData: priceMap[hrt.hrtId].priceData,
          });
          throw new Error("Price data not found");
        }

        const cashOverride = false;
        const basePrice = priceData.price;

        // Only use finalCartItem if it matches the user's selected payment mode
        // If user selected CASH but finalCartItem is INSURED, use priceMap data instead
        if (
          finalCartItem &&
          finalCartItem.paymentType === selectedPaymentMode
        ) {
          // finalCartItem prices come straight from the getCartPrice server
          // round-trip, which has no knowledge of storefront discount codes, so
          // no discount is ever applied here (this was already true for PERCENT
          // codes). The shared DOLLAR budget is intentionally left intact: it
          // only ever shrinks, and since these items take $0 off, the cart-wide
          // total discount can never exceed the code's face value — a "$100 off"
          // code cannot be applied twice. The discount just isn't attributed to
          // server-priced items. applyDiscount is computed only below, after
          // this early-return, so nothing is calculated and discarded here.
          shadowTotalPrice += finalCartItem.price / 100;
          return {
            id: finalCartItem.id,
            hrts: [hrt] as unknown as HrtExtended[],
            paymentType: finalCartItem.paymentType,
            cashOverride,
            pricedTreatmentId: finalCartItem.id,
            price: finalCartItem.price / 100,
            originalPrice: finalCartItem.price / 100,
            discountCodeApplied: undefined,
            ehrPrimaryAppointmentTypeId: hrt.ehrPrimaryAppointmentTypeId[0],
          };
        }

        // The new rules: discounts on self-pay items only, rounded per item.
        const canDiscountItem =
          isDiscountApplied && isDiscountablePaymentMode(paymentType);

        const { price: discountedPrice, isDiscounted } = shadowMode
          ? applyDiscountLegacy(
              basePrice,
              hrt.hrtId,
              isDiscountApplied,
              discountData,
              remainingDollarDiscount,
            )
          : applyDiscount(
              basePrice,
              hrt.hrtId,
              canDiscountItem,
              discountData,
              remainingDollarDiscount,
            );

        // Draw down the shared DOLLAR budget by the amount this item actually
        // used, so later items only see what's left. PERCENT codes don't touch
        // the budget.
        if (
          discountData?.discountType === DiscountType.Dollar &&
          isDiscounted
        ) {
          remainingDollarDiscount = shadowMode
            ? remainingDollarDiscount - (basePrice - discountedPrice)
            : roundToNearestCent(
                remainingDollarDiscount - (basePrice - discountedPrice),
              );
        }

        if (shadowMode) {
          const proposed = applyDiscount(
            basePrice,
            hrt.hrtId,
            canDiscountItem,
            discountData,
            shadowRemainingDollarDiscount,
          );
          if (
            discountData?.discountType === DiscountType.Dollar &&
            proposed.isDiscounted
          ) {
            shadowRemainingDollarDiscount = roundToNearestCent(
              shadowRemainingDollarDiscount - (basePrice - proposed.price),
            );
          }
          shadowTotalPrice += proposed.price;
        }

        return {
          id: pricedTreatmentId,
          hrts: [hrt] as unknown as HrtExtended[],
          paymentType: isDiscounted ? "SELFPAYDISCOUNTED" : paymentType,
          cashOverride,
          pricedTreatmentId,
          price: discountedPrice,
          originalPrice: originalCashPrice ?? discountedPrice,
          discountCodeApplied: discountData?.code,
          ehrPrimaryAppointmentTypeId: hrt.ehrPrimaryAppointmentTypeId[0],
        };
      })
    : [];

  return {
    cartItems,
    isLoading,
    isLoadingCartPrice,
    shadowTotalPrice: shadowMode && hrtData ? shadowTotalPrice : undefined,
  };
}
